/** Tutorial Flow 3 — step execution, prerequisites, and resume-from-step helpers. */

import { FLOW3_PIPELINE_STEPS } from '@/lib/learningTutorial/flow3PipelineSteps';

export const FLOW3_RUN_STEPS = [
  'concept_tree_v3',
  'knowledge_analysis_v3',
  'course_structure_v3',
  'slide_architecture_v3',
  'curriculum_optimization_v3',
  'artifact_specification_v3',
  'artifact_generation_v3',
  'artifact_rendering_v3',
  'artifact_edm_v3',
  'artifact_authoring_v3',
  'artifact_studio_v3',
  'artifact_layout_engine_v3',
  'artifact_svg_renderer_v3',
  'artifact_rrao_v3',
];

/** Steps to run when continuing from startStepId (inclusive). */
export function flow3StepsFrom(startStepId) {
  const idx = FLOW3_RUN_STEPS.indexOf(startStepId);
  if (idx < 0) return [];
  return FLOW3_RUN_STEPS.slice(idx);
}

/** Steps strictly after startStepId. */
export function flow3StepsAfter(startStepId) {
  const idx = FLOW3_RUN_STEPS.indexOf(startStepId);
  if (idx < 0) return [];
  return FLOW3_RUN_STEPS.slice(idx + 1);
}

export function flow3StepMeta(stepId) {
  return FLOW3_PIPELINE_STEPS.find((s) => s.id === stepId) || {
    id: stepId,
    shortLabel: stepId,
    label: stepId,
  };
}

export function flow3StepComplete(
  stepId,
  {
    conceptTree,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    artifactLayoutEngine,
    artifactSvgRenderer,
    artifactRrao,
  } = {},
) {
  if (stepId === 'concept_tree_v3') return Boolean(conceptTree?.concept_tree);
  if (stepId === 'knowledge_analysis_v3') return Boolean(knowledgeAnalysis?.concepts?.length);
  if (stepId === 'course_structure_v3') return Boolean(courseStructure?.modules?.length);
  if (stepId === 'slide_architecture_v3') return Boolean(slideArchitecture?.slides?.length);
  if (stepId === 'curriculum_optimization_v3') {
    return Boolean(curriculumOptimization?.updated_slide_architecture?.slides?.length
      || curriculumOptimization?.final_curriculum_readiness_score != null);
  }
  if (stepId === 'artifact_specification_v3') {
    return Boolean(artifactSpecification?.slide_specifications?.length);
  }
  if (stepId === 'artifact_generation_v3') {
    return Boolean(artifactGeneration?.artifact_knowledge_objects?.length);
  }
  if (stepId === 'artifact_rendering_v3') {
    return Boolean(artifactRendering?.rendered_artifact_objects?.length);
  }
  if (stepId === 'artifact_edm_v3') {
    return Boolean(artifactEdm?.editable_diagram_models?.length);
  }
  if (stepId === 'artifact_authoring_v3') {
    return Boolean(artifactAuthoring?.published_artifacts?.length);
  }
  if (stepId === 'artifact_studio_v3') {
    return Boolean(artifactStudio?.studio_entries?.length);
  }
  if (stepId === 'artifact_layout_engine_v3') {
    return Boolean(artifactLayoutEngine?.layout_entries?.length);
  }
  if (stepId === 'artifact_svg_renderer_v3') {
    return Boolean(artifactSvgRenderer?.render_entries?.length);
  }
  if (stepId === 'artifact_rrao_v3') {
    return Boolean(artifactRrao?.rrao_entries?.length);
  }
  return false;
}

export function flow3ProgressPct({
  conceptTree,
  knowledgeAnalysis,
  courseStructure,
  slideArchitecture,
  curriculumOptimization,
  artifactSpecification,
  artifactGeneration,
  artifactRendering,
  artifactEdm,
  artifactAuthoring,
  artifactStudio,
  artifactLayoutEngine,
  artifactSvgRenderer,
  artifactRrao,
} = {}) {
  const total = FLOW3_RUN_STEPS.length;
  let done = 0;
  for (const id of FLOW3_RUN_STEPS) {
    if (flow3StepComplete(id, {
      conceptTree,
      knowledgeAnalysis,
      courseStructure,
      slideArchitecture,
      curriculumOptimization,
      artifactSpecification,
      artifactGeneration,
      artifactRendering,
      artifactEdm,
      artifactAuthoring,
      artifactStudio,
      artifactLayoutEngine,
      artifactSvgRenderer,
      artifactRrao,
    })) {
      done += 1;
    }
  }
  return Math.round((done / total) * 100);
}

/**
 * Whether a step can be executed now.
 * @returns {{ ok: boolean, reason?: string }}
 */
export function flow3CanRunStep(
  stepId,
  {
    tutorialName = '',
    topic = '',
    conceptTree = null,
    knowledgeAnalysis = null,
    courseStructure = null,
    slideArchitecture = null,
    curriculumOptimization = null,
    artifactSpecification = null,
    artifactGeneration = null,
    artifactRendering = null,
    artifactEdm = null,
    artifactAuthoring = null,
    artifactStudio = null,
    artifactLayoutEngine = null,
    artifactSvgRenderer = null,
    pipelineLoading = false,
  } = {},
) {
  if (pipelineLoading) {
    return { ok: false, reason: 'Pipeline is already running' };
  }
  if (stepId === 'concept_tree_v3') {
    const name = (tutorialName || '').trim();
    const subj = (topic || '').trim();
    if (!name) return { ok: false, reason: 'Tutorial name is required' };
    if (subj.length < 2) return { ok: false, reason: 'Topic must be at least 2 characters' };
    return { ok: true };
  }
  if (stepId === 'knowledge_analysis_v3') {
    if (!conceptTree?.concept_tree) {
      return { ok: false, reason: 'Run Step 0 (concept tree) first' };
    }
    return { ok: true };
  }
  if (stepId === 'course_structure_v3') {
    if (!conceptTree?.concept_tree) return { ok: false, reason: 'Concept tree required' };
    if (!knowledgeAnalysis?.concepts?.length) {
      return { ok: false, reason: 'Run Step 1 (knowledge analysis) first' };
    }
    return { ok: true };
  }
  if (stepId === 'slide_architecture_v3') {
    if (!courseStructure?.modules?.length) {
      return { ok: false, reason: 'Run Step 2 (course structure) first' };
    }
    return { ok: true };
  }
  if (stepId === 'curriculum_optimization_v3') {
    if (!slideArchitecture?.slides?.length) {
      return { ok: false, reason: 'Run Step 3 (slide architecture) first' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_specification_v3') {
    if (!curriculumOptimization?.updated_slide_architecture?.slides?.length
      && curriculumOptimization?.final_curriculum_readiness_score == null) {
      return { ok: false, reason: 'Run Step 3.5 (curriculum optimization) first' };
    }
    if (!knowledgeAnalysis?.concepts?.length) {
      return { ok: false, reason: 'Knowledge analysis required' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_generation_v3') {
    if (!artifactSpecification?.slide_specifications?.length) {
      return { ok: false, reason: 'Run Step 4 (artifact specification) first' };
    }
    if (!knowledgeAnalysis?.concepts?.length) {
      return { ok: false, reason: 'Knowledge analysis required' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_rendering_v3') {
    if (!artifactGeneration?.artifact_knowledge_objects?.length) {
      return { ok: false, reason: 'Run Step 5A (AKO generation) first' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_edm_v3') {
    if (!artifactRendering?.rendered_artifact_objects?.length) {
      return { ok: false, reason: 'Run Step 5B (Artifact Rendering Plan) first' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_authoring_v3') {
    if (!artifactEdm?.editable_diagram_models?.length) {
      return { ok: false, reason: 'Run Step 5C.1 (EDM generation) first' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_studio_v3') {
    if (!artifactAuthoring?.published_artifacts?.length) {
      return { ok: false, reason: 'Run Step 5C.2 (Artifact Authoring API) first' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_layout_engine_v3') {
    if (!artifactStudio?.studio_entries?.length) {
      return { ok: false, reason: 'Run Step 5C.3 (Artifact Authoring Studio) first' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_svg_renderer_v3') {
    if (!artifactLayoutEngine?.layout_entries?.length) {
      return { ok: false, reason: 'Run Step 5C.4 (Layout Engine) first' };
    }
    return { ok: true };
  }
  if (stepId === 'artifact_rrao_v3') {
    if (!artifactSvgRenderer?.render_entries?.length) {
      return { ok: false, reason: 'Run Step 5C.5 (SVG Renderer) first' };
    }
    return { ok: true };
  }
  return { ok: false, reason: 'Unknown step' };
}

/** Next incomplete step, or null if pipeline complete. */
export function flow3ResolveResumeStep({
  conceptTree,
  knowledgeAnalysis,
  courseStructure,
  slideArchitecture,
  curriculumOptimization,
  artifactSpecification,
  artifactGeneration,
  artifactRendering,
  artifactEdm,
  artifactAuthoring,
  artifactStudio,
  artifactLayoutEngine,
  artifactSvgRenderer,
  artifactRrao,
} = {}) {
  for (const id of FLOW3_RUN_STEPS) {
    if (!flow3StepComplete(id, {
      conceptTree,
      knowledgeAnalysis,
      courseStructure,
      slideArchitecture,
      curriculumOptimization,
      artifactSpecification,
      artifactGeneration,
      artifactRendering,
      artifactEdm,
      artifactAuthoring,
      artifactStudio,
      artifactLayoutEngine,
      artifactSvgRenderer,
      artifactRrao,
    })) {
      return id;
    }
  }
  return null;
}
